import type { Ref } from "react";
import { ImageViewer } from "./image-viewer";
import { DocxViewer, PdfViewer, XlsxViewer } from "./lazy";
import type { PdfViewerHandle, PdfZoom } from "./pdf-types";
import type { DocumentSource, PdfHighlight, XlsxViewerProps } from "./types";

/**
 * `csv` is a delimited file (CSV, TSV): drawn by the workbook viewer, which
 * opens only .xlsx, from the file converted to a workbook (`workbook`).
 */
export type DocumentViewerKind = "pdf" | "xlsx" | "csv" | "docx" | "image";

const BY_EXTENSION: Record<string, DocumentViewerKind> = {
  pdf: "pdf",
  xlsx: "xlsx",
  xlsm: "xlsx",
  csv: "csv",
  tsv: "csv",
  docx: "docx",
  png: "image",
  jpg: "image",
  jpeg: "image",
  gif: "image",
  webp: "image",
};

const BY_TYPE: Record<string, DocumentViewerKind> = {
  "application/pdf": "pdf",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/vnd.ms-excel.sheet.macroenabled.12": "xlsx",
  "text/csv": "csv",
  "text/tab-separated-values": "csv",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "docx",
  "image/png": "image",
  "image/jpeg": "image",
  "image/gif": "image",
  "image/webp": "image",
};

/**
 * Which viewer shows a file, from its content type or, failing that, its
 * extension. Null for a file no viewer draws (text, TIFF, PowerPoint): show
 * what was read from it instead. A CSV or TSV is `csv`: give the viewer the
 * file converted to .xlsx as `workbook`.
 */
export const documentViewerKind = ({
  contentType,
  fileName,
}: {
  contentType?: string | null;
  fileName?: string | null;
}): DocumentViewerKind | null => {
  const type = contentType?.split(";")[0]?.trim().toLowerCase();
  if (type && BY_TYPE[type]) return BY_TYPE[type];
  const extension = fileName?.split(".").pop()?.toLowerCase();
  return (extension && BY_EXTENSION[extension]) || null;
};

export type DocumentViewerProps = XlsxViewerProps & {
  kind: DocumentViewerKind;
  /** PDF: the page to show (1-based). */
  page?: number;
  /** PDF: boxes drawn over the pages. */
  highlights?: PdfHighlight[];
  /** PDF: text to search for and highlight. */
  searchText?: string;
  defaultZoom?: PdfZoom;
  pdfRef?: Ref<PdfViewerHandle>;
  /**
   * CSV: the file converted to an .xlsx workbook, which the workbook viewer
   * draws (it opens only .xlsx), with its .xlsx name. `src` and `data` are
   * the original file, which it cannot read.
   */
  workbook?: DocumentSource;
};

/**
 * The viewer for a file's kind, with the props that kind takes: a PDF's
 * page, highlights and search, a workbook's sheet, cell and tinted rows (a
 * CSV's too, drawn from its `workbook`).
 */
export const DocumentViewer = ({
  kind,
  page,
  highlights,
  searchText,
  defaultZoom,
  pdfRef,
  activeSheet,
  selectedCell,
  highlightRow,
  highlightRows,
  blanks,
  workbook,
  ...source
}: DocumentViewerProps) => {
  const cell = {
    activeSheet,
    selectedCell,
    highlightRow,
    highlightRows,
    blanks,
  };
  switch (kind) {
    case "pdf":
      return (
        <PdfViewer
          {...source}
          ref={pdfRef}
          page={page}
          highlights={highlights}
          searchText={searchText}
          defaultZoom={defaultZoom}
        />
      );
    case "xlsx":
      return <XlsxViewer {...source} {...cell} />;
    case "csv":
      return (
        <XlsxViewer
          {...source}
          src={workbook?.src}
          data={workbook?.data}
          fileName={workbook?.fileName ?? source.fileName}
          {...cell}
        />
      );
    case "docx":
      return <DocxViewer {...source} />;
    case "image":
      return <ImageViewer {...source} />;
  }
};
